"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Button } from "src/components/buttons/shadcn-button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "src/components/form/form";
import { Input } from "src/components/input/input";
import { MonthYearPicker } from "src/components/ui/MonthYearPicker";
import { editPayroll } from "src/redux/api/_hr.api";
import { Payroll } from "src/redux/types/payroll";
import { PayrollFormData } from "src/schemas/add-payroll-form";
import { EditPayrollSchema } from "src/schemas/edit-payroll";
import NumberToWords from "src/utils/NumberToWord";
import { z } from "zod";

interface EditPayrollFormProps {
  payroll: Payroll;
}

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getDaysInMonth = (month: string, year: number): number => {
  const monthIndex = months.indexOf(month);
  if (monthIndex === -1) return 0; // Invalid month

  if (monthIndex === 1) {
    const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return isLeap ? 29 : 28;
  }

  const daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return daysInMonths[monthIndex];
};

export default function EditPayrollForm({
  payroll,
}: EditPayrollFormProps) {
  const form = useForm<z.infer<typeof EditPayrollSchema>>({
    resolver: zodResolver(EditPayrollSchema),
    defaultValues: {
      ...payroll,
      grossSalary: payroll.gross_salary,
      date_of_joining: payroll?.date_of_joining
        ? new Date(payroll?.date_of_joining).toISOString().split("T")[0]
        : "",
      pay_date: payroll?.pay_date
        ? new Date(payroll?.pay_date).toISOString().split("T")[0]
        : "",
      esic_uan: payroll.esic_uan,
      pf_acc_number: payroll.pf_account_number,
      other_deductions: payroll.other_deduction || 0,
      lop: payroll.lop || 0,
    },
  });

  const { watch, handleSubmit, setValue } = form;
  const values = watch();

  const [totalDaysInMonth, setTotalDaysInMonth] = useState(0);

  const netSalary = useMemo(() => {
    const allowances =
      Number(values.statutory_bonus || 0) +
      Number(values.incentives || 0) +
      Number(values.misc_allowance || 0) +
      Number(values.travel_allowances || 0) +
      Number(values.otherEarnings || 0) +
      Number(values.reimbursementTravel || 0) +
      Number(values.reimbursementOthers || 0);

    const deductions =
      Number(values.pf || 0) +
      Number(values.esic || 0) +
      Number(values.ap_tax || 0) +
      Number(values.health_deduction || 0) +
      Number(values.other_deductions || 0) +
      Number(values.lop_deduction || 0) +
      Number(values.incomeTaxDeduction || 0);

    const total = (Number(values.grossSalary) || 0) + allowances - deductions;
    return Math.max(0, total);
  }, [values]);

  const netSalaryValue = form.watch("net_salary");

  const onSubmit = async (values: z.infer<typeof EditPayrollSchema>) => {
    try {
      const res = await editPayroll(values, payroll.id);
      if (res.message.toLowerCase() === "success") {
        toast.success("Payroll Updated!");
      }
    } catch (error) {
      toast.error("Failed to Update Payroll");
      console.error(error);
    }
  };

  useEffect(() => {
    if (netSalaryValue !== undefined && !isNaN(Number(netSalaryValue))) {
      setValue(
        "totalNetPayableWords",
        NumberToWords(netSalaryValue),
        {
          shouldValidate: true,
        },
      );
    }
  }, [netSalaryValue, setValue]);

  useEffect(() => {
    const gross = Number(values.grossSalary ?? 0);
    if (gross > 0) {
      const basic = gross * 0.5;
      const hra = basic * 0.4;
      const specialAllowance = gross - (basic + hra);

      setValue("basic", Number(basic.toFixed(2)), { shouldDirty: true });
      setValue("hra", Number(hra.toFixed(2)), { shouldDirty: true });
      setValue("special_allowance", Number(specialAllowance.toFixed(2)), {
        shouldDirty: true,
      });
    }
  }, [values.grossSalary, setValue]);

  useEffect(() => {
    const gross = Number(values.grossSalary || 0);

    if (values.days_worked === 0 || gross === 0) {
      setValue("ap_tax", 0, { shouldDirty: true });
      return;
    }

    let apTax = 0;

    if (gross >= 0 && gross <= 15000) apTax = 0;
    else if (gross > 15000 && gross <= 25000) apTax = 180;
    else if (gross > 25000) apTax = 208;
    setValue("ap_tax", apTax, { shouldDirty: true });
  }, [values.grossSalary, values.days_worked, setValue]);

  useEffect(() => {
    const month = values.month;
    const year = values.year;

    if (month && year) {
      const days = getDaysInMonth(month, Number(year));
      setTotalDaysInMonth(days);

      // ✅ clamp lop to never exceed total days in month
      const lop = Math.min(Number(values.lop || 0), days);

      // ✅ if lop was out of range, correct it in the form
      if (Number(values.lop || 0) > days) {
        setValue("lop", days, { shouldDirty: true });
      }

      const daysWorked = days - lop;
      setValue("days_worked", daysWorked, { shouldDirty: true });
    }
  }, [values.month, values.year, values.lop, setValue]);

  useEffect(() => {
    const gross = Number(values.grossSalary || 0);
    const lop = Math.min(Number(values.lop || 0), totalDaysInMonth); // ✅ clamped lop

    if (totalDaysInMonth > 0 && gross > 0 && lop >= 0) {
      const perDayIncome = gross / totalDaysInMonth; // ✅ use actual days, not hardcoded 30
      const lopDeduction = perDayIncome * lop;

      setValue("lop_deduction", Number(lopDeduction.toFixed(2)), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    // ✅ reset lop_deduction when no lop
    if (lop === 0) {
      setValue("lop_deduction", 0, { shouldDirty: true });
    }
  }, [values.grossSalary, values.lop, totalDaysInMonth, setValue]);

  useEffect(() => {
    if (!isNaN(netSalary)) {
      setValue("net_salary", netSalary, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [netSalary, setValue]);

  return (
    <div className="bg-card shadow-lg rounded-xl p-6 space-y-4 border border-gray-200">
      <Form {...form}>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="">
            <h1 className="text-lg font-semibold mb-3">Payroll Month & Year</h1>
            <FormField
              control={form.control}
              name="month"
              render={() => (
                <FormItem>
                  <MonthYearPicker nameMonth="month" nameYear="year" />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <h4 className="text-lg font-semibold mt-4">Employee Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { name: "name", label: "Full Name" },
              { name: "designation", label: "Designation" },
              { name: "department", label: "Department" },
              {
                name: "employee_code",
                label: "Employee Code",
                type: "text",
              },
              { name: "pan", label: "PAN" },
              {
                name: "date_of_joining",
                label: "Date of Joining",
                type: "date",
              },
              { name: "esic_uan", label: "ESIC UAN" },
              { name: "pf_acc_number", label: "P.F. A/C Number" },
              { name: "uan_number", label: "UAN Number" },
            ].map((item) => (
              <FormField
                key={item.name}
                control={form.control}
                name={item.name as keyof PayrollFormData}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{item.label}</FormLabel>
                    <FormControl>
                      <Input
                        readOnly
                        {...field}
                        type={item.type || "text"}
                        className="bg-gray-100"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>

          <h4 className="text-lg font-semibold mt-6">Earnings</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: "grossSalary", label: "Gross Salary" },
              { name: "basic", label: "Basic", readOnly: true },
              { name: "hra", label: "HRA", readOnly: true },
              {
                name: "special_allowance",
                label: "Special Allowance",
                readOnly: true,
              },
              { name: "statutory_bonus", label: "Statutory Bonus" },
              { name: "incentives", label: "Incentives" },
              {
                name: "misc_allowance",
                label: "Miscellaneous Allowance",
              },
              { name: "travel_allowances", label: "Travel Allowance" },
              { name: "otherEarnings", label: "Other Earnings" },
            ].map((item) => (
              <FormField
                key={item.name}
                control={form.control}
                name={item.name as keyof PayrollFormData}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{item.label}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        readOnly={item.readOnly}
                        className={item.readOnly ? "bg-gray-100" : ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>

          <h4 className="text-lg font-semibold mt-6">Deductions</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { name: "pf", label: "PF" },
              { name: "esic", label: "ESIC" },
              { name: "ap_tax", label: "AP Tax", readOnly: true },
              { name: "health_deduction", label: "Healthcare Deduction" },
            ].map((item) => (
              <FormField
                key={item.name}
                control={form.control}
                name={item.name as keyof PayrollFormData}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{item.label}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        readOnly={item.readOnly}
                        className={item.readOnly ? "bg-gray-100" : ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}

            <FormField
              control={form.control}
              name="lop_deduction"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>LOP Deduction</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      readOnly
                      {...field}
                      className="bg-gray-100"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="other_deductions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Other Deductions</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="incomeTaxDeduction"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Income Tax Deduction</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <h4 className="text-lg font-semibold mt-6">Reimbursements</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { name: "reimbursementTravel", label: "Reimbursement (Travel)" },
              { name: "reimbursementOthers", label: "Reimbursement (Others)" },
            ].map((item) => (
              <FormField
                key={item.name}
                control={form.control}
                name={item.name as keyof PayrollFormData}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{item.label}</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>

          <h4 className="text-lg font-semibold mt-6">
            Net Salary & Pay Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormItem>
              <FormLabel>Net Salary</FormLabel>
              <FormControl>
                <Input
                  value={netSalary}
                  {...form.register("net_salary")}
                  readOnly
                  className="bg-gray-100"
                />
              </FormControl>
            </FormItem>

            <FormField
              control={form.control}
              name="totalNetPayableWords"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Total Net Payable (in words)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. Twenty Five Thousand Only"
                      {...field}
                      readOnly
                      className="bg-gray-100"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <FormField
              control={form.control}
              name="pay_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pay Date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="days_worked"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Days Worked</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      readOnly
                      className="bg-gray-100"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lop"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>LOP (in day/s)</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <h4 className="text-lg font-semibold mt-6">Bank Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="bank_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bank Name</FormLabel>
                  <FormControl>
                    <Input readOnly {...field} className="bg-gray-50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="ac_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Number</FormLabel>
                  <FormControl>
                    <Input readOnly {...field} className="bg-gray-50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="ifsc_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>IFSC Code</FormLabel>
                  <FormControl>
                    <Input readOnly {...field} className="bg-gray-50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="branch"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Branch</FormLabel>
                  <FormControl>
                    <Input readOnly {...field} className="bg-gray-50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Submit */}
          <div className="flex justify-center mt-6">
            <Button
              type="submit"
              className="px-12"
              disabled={form.formState.isSubmitting}
              onClick={() => console.log(form.formState.errors)}
            >
              {form.formState.isSubmitting ? (
                <span className="loading loading-dots loading-md"></span>
              ) : (
                "Update"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
